import React, { useState, useEffect, useRef } from 'react';
import './styles.scss';

// Custom hook for scroll animations
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        if (options.once !== false) {
          observer.unobserve(entry.target);
        }
      } else if (options.once === false) {
        setIsInView(false);
      }
    }, {
      threshold: options.threshold || 0.2,
      rootMargin: options.rootMargin || '0px'
    });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isInView];
};

const Scoutify = () => {
  // Animation refs
  const [phoneRef, phoneInView] = useInView({ threshold: 0.3, rootMargin: '-50px 0px' });
  const [logoRef, logoInView] = useInView({ threshold: 0.5, rootMargin: '-50px 0px' });
  const [playersRef, playersInView] = useInView({ threshold: 0.3, rootMargin: '-50px 0px' });
  const [brandsRef, brandsInView] = useInView({ threshold: 0.4, rootMargin: '-50px 0px' });
  const [descRef, descInView] = useInView({ threshold: 0.5, rootMargin: '-50px 0px' });

  return (
    <section className="scoutify-section">
      {/* Phone mockup - OUTSIDE scoutify-top so it's not clipped */}
      <div 
        ref={phoneRef}
        className={`phone-container animate-fade-up ${phoneInView ? 'is-visible' : ''}`}
      >
        <img 
          src="/2025/scoutify/icons/phoneScreen.png" 
          alt="Scoutify App Screen" 
          className="phone-mockup"
          loading="lazy"
        />
      </div>

      {/* ===== TOP SECTION (gets clipped) ===== */}
      <div className="scoutify-top">
        {/* Logo */}
        <div 
          ref={logoRef}
          className={`logo-container animate-fade-up ${logoInView ? 'is-visible' : ''}`}
        >
          <img 
            src="/2025/scoutify/icons/logo.svg" 
            alt="Scoutify Logo" 
            className="scoutify-logo"
          />
        </div>

        {/* Right side: Players image */}
        <div 
          ref={playersRef}
          className={`right-side animate-slide-right ${playersInView ? 'is-visible' : ''}`}
        >
          <img 
            src="/2025/scoutify/images/players.png" 
            alt="Athletes" 
            className="players-image"
            loading="lazy"
          />
        </div>

        {/* Yellow diagonal decoration - overlays everything inside scoutify-top */}
        <div className="yellow-decoration-wrapper">
          <img 
            src="/2025/scoutify/icons/yellowBackground.svg" 
            alt="" 
            className="yellow-decoration"
          />
        </div>
      </div>

      {/* ===== YELLOW STRIPE DIVIDER ===== */}
      <div className="yellow-stripe"></div>

      {/* ===== BOTTOM SECTION ===== */}
      <div className="scoutify-bottom">
        {/* Brand logos */}
        <div 
          ref={brandsRef}
          className={`brands-row animate-stagger-brands ${brandsInView ? 'is-visible' : ''}`}
        >
          <img src="/2025/scoutify/icons/adidas.png" alt="Adidas" className="brand-logo" />
          <img src="/2025/scoutify/icons/puma.png" alt="Puma" className="brand-logo" />
          <img src="/2025/scoutify/icons/On.svg" alt="On Running" className="brand-logo on-logo" />
          <img src="/2025/scoutify/icons/wg.png" alt="WMG" className="brand-logo" />
          <img src="/2025/scoutify/icons/wme.png" alt="WME" className="brand-logo" />
        </div>

        {/* Description text */}
        <p 
          ref={descRef}
          className={`description-text animate-fade-up ${descInView ? 'is-visible' : ''}`}
        >
          Scoutify улеснява проследяването, оценяването и управлението на представянето на атлетите. С колекция от данни в реално време, персонализирани профили на атлети и интуитивни функции за отчитане, приложението рационализира процеса на скаутиране и помага на треньорите и специалистите да вземат информирани решения бързо и ефективно.
        </p>
      </div>
    </section>
  );
};

export default Scoutify;
